import { ErrorOutlineIcon } from '@applyboard/ui-icons';
import { keyframes } from '@emotion/react';
import React from 'react';
import { Flex } from '../Flex';
import { HelpText } from '../HelpText';
import { Text } from '../Text';
import { useFullTheme } from '~/design';
import { clamp, useId } from '~/utils';
import { IconWrapper } from '~/internal';

export interface ProgressIndicatorProps {
  /** The alignment of the indicator with the other elements. */
  alignment?: 'default' | 'inline';

  /** Used when you don't want to show a visible label */
  'aria-label'?: string;

  /** Help text associated with the progress indicator. */
  helpText?: string;

  /**
   * If true, the progress indicator will use a fill of 50% and animate the fill back and forth.
   * Useful to represent a "loading" state.
   */
  indeterminate?: boolean;

  /** Whether or not the progress indicator should be displayed as invalid. */
  invalid?: boolean;

  /** The label describing what the progress indicator is for. */
  label?: string;

  /** The size of the progress indicator. */
  size?: 'sm' | 'lg';

  /** The number to divide `value` by to determine the progress "amount". (default: 100) */
  total?: number;

  /**
   * How much progress to display. Translates to how much of the underlying shape has been filled.
   * Not applicable if `indeterminate = true`.
   */
  value?: number;

  /** The format of the indicator progress. Only visible if `indeterminate = false` */
  valueFormat?: 'percentage' | 'number';

  /** The variant of the progress indicator. */
  variant: 'linear' | 'circular';
}

interface InternalIndicatorProps extends Omit<ProgressIndicatorProps, 'variant'> {
  color: string;
}

interface ValueLabelProps extends Pick<ProgressIndicatorProps, 'value' | 'valueFormat'> {
  align?: 'start' | 'end';
}

export function ProgressIndicator({
  alignment = 'default',
  'aria-label': ariaLabel,
  helpText,
  indeterminate = false,
  invalid = false,
  label,
  size = 'sm',
  total,
  value: _value = 0,
  valueFormat = 'percentage',
  variant,
}: ProgressIndicatorProps) {
  if ((!label && !ariaLabel) || (label && ariaLabel)) {
    throw new Error('One of label or aria-label is required.');
  }

  const theme = useFullTheme();
  const value = Math.floor(clamp(_value, 0, 100));
  const intent = invalid ? 'negative' : value === 100 ? 'positive' : 'primary';

  const color = {
    primary: theme.colors.actionPrimary,
    positive: theme.colors.borderPositive,
    negative: theme.colors.actionNegative,
  }[intent];

  const Tag = variant === 'linear' ? LinearProgressIndicator : CircularProgressIndicator;
  return (
    <Tag
      alignment={alignment}
      color={color}
      helpText={helpText}
      indeterminate={indeterminate}
      invalid={invalid}
      label={label}
      aria-label={ariaLabel}
      size={size}
      total={total}
      value={value}
      valueFormat={valueFormat}
    />
  );
}

function LinearProgressIndicator({
  alignment,
  color,
  helpText,
  indeterminate,
  invalid,
  label,
  'aria-label': ariaLabel,
  size = 'sm',
  total = 100,
  value = 0,
  valueFormat,
}: InternalIndicatorProps) {
  const percentageValue = Math.min(100 * (value / total), 100);
  const { a11yProps, helpTextId, labelId } = useA11y({
    helpText,
    indeterminate,
    value: percentageValue,
    ariaLabel,
  });
  const theme = useFullTheme();

  const barWidth = indeterminate ? '50%' : value === 0 ? '0px' : `${percentageValue}%`;

  const isInline = alignment === 'inline';

  const sizeInPx = {
    sm: 4,
    lg: 8,
  }[size];

  const bars = (
    <div css={{ height: `${sizeInPx}px`, flexGrow: 1, position: 'relative' }}>
      <div css={{ backgroundColor: theme.colors.borderRegular, height: `${sizeInPx}px` }}></div>
      <div
        css={[
          {
            backgroundColor: color,
            height: `${sizeInPx}px`,
            position: 'absolute',
            top: 0,
            width: barWidth,
          },
          indeterminate && {
            animationDuration: '0.7s',
            animationName: barAnimation,
            animationIterationCount: 'infinite',
            animationDirection: 'alternate',
            animationTimingFunction: 'linear',
          },
        ]}
        data-testid="cds-progress-indicator-bar"
      />
    </div>
  );

  const textAlign = label ? 'end' : 'start';

  return (
    <div
      {...a11yProps}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[2],
      }}
    >
      <Flex align="center" gap={isInline ? 2 : 0} justify={isInline ? undefined : 'between'}>
        {label && (
          <Text id={labelId} variant="labelM">
            {label}
          </Text>
        )}

        {isInline && bars}

        {!indeterminate && (
          <div css={{ height: '24px', textAlign, width: '36px', whiteSpace: 'nowrap' }}>
            {invalid ? (
              <IconWrapper allowInteractions={false} intent="negative" selector="div">
                <ErrorOutlineIcon />
              </IconWrapper>
            ) : (
              <ValueLabel
                align={textAlign}
                value={valueFormat === 'number' ? value : Math.trunc(percentageValue)}
                valueFormat={valueFormat}
              />
            )}
          </div>
        )}
      </Flex>

      {!isInline && bars}

      {helpText && (
        <HelpText id={helpTextId} intent={invalid ? 'negative' : 'secondary'}>
          {helpText}
        </HelpText>
      )}
    </div>
  );
}

function CircularProgressIndicator({
  alignment,
  color,
  helpText,
  indeterminate,
  invalid,
  label,
  size = 'sm',
  total = 100,
  value: _value = 0,
  valueFormat,
  'aria-label': ariaLabel,
}: InternalIndicatorProps) {
  const percentageValue = indeterminate ? 25 : Math.min(100 * (_value / total), 100);
  const { a11yProps, helpTextId, labelId } = useA11y({
    helpText,
    indeterminate,
    value: percentageValue,
    ariaLabel,
  });
  const theme = useFullTheme();
  const value = indeterminate ? 50 : _value ?? 0;

  const width = {
    sm: 4,
    lg: 8,
  }[size];
  const sizeInPx = {
    sm: 24,
    lg: 48,
  }[size];

  // Subtract the stroke width
  const radius = sizeInPx / 2 - width / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentageValue / 100) * circumference;

  const circles = (
    <div css={{ flexShrink: 0, height: `${sizeInPx}px`, width: `${sizeInPx}px` }}>
      <svg height={sizeInPx} width={sizeInPx}>
        <circle
          strokeWidth={width}
          fill="transparent"
          stroke={theme.colors.borderRegular}
          r={radius}
          cx={sizeInPx / 2}
          cy={sizeInPx / 2}
        />
        <circle
          css={[
            {
              strokeDasharray: `${circumference} ${circumference}`,
              strokeDashoffset: offset,
              transform: 'rotate(270deg)',
              transformOrigin: '50% 50%',
            },
            indeterminate
              ? {
                  animationDuration: '0.7s',
                  animationName: circleAnimation,
                  animationIterationCount: 'infinite',
                  animationDirection: 'normal',
                  animationTimingFunction: 'linear',
                }
              : {
                  transition: 'stroke-dashoffset 0.35s',
                },
          ]}
          cx={sizeInPx / 2}
          cy={sizeInPx / 2}
          data-testid="cds-progress-indicator-circle"
          fill="transparent"
          r={radius}
          stroke={color}
          strokeWidth={width}
        />
      </svg>
    </div>
  );

  const percentageText = (
    <ValueLabel
      value={valueFormat === 'number' ? value : Math.trunc(percentageValue)}
      valueFormat={valueFormat}
    />
  );
  const helpTextElement = helpText ? (
    <HelpText id={helpTextId} intent={invalid ? 'negative' : 'secondary'}>
      {helpText}
    </HelpText>
  ) : null;

  if (alignment === 'inline') {
    return (
      <div
        {...a11yProps}
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing[2],
        }}
      >
        <Flex align="center" gap={2}>
          {label && (
            <Text id={labelId} variant="labelM">
              {label}
            </Text>
          )}
          {circles}
          {invalid ? <ErrorOutlineIcon intent="negative" /> : indeterminate ? null : percentageText}
        </Flex>

        {helpTextElement}
      </div>
    );
  }

  return (
    <div
      {...a11yProps}
      css={{
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing[2],
      }}
    >
      {circles}

      {(!!label || !!helpTextElement || invalid || !indeterminate) && (
        <Flex direction="column" gap={1} grow={1}>
          {(label || invalid) && (
            <Flex justify={label ? 'between' : 'start'}>
              {!!label && (
                <Text id={labelId} variant="labelM">
                  {label}
                </Text>
              )}

              {invalid && <ErrorOutlineIcon intent="negative" />}
            </Flex>
          )}

          {!indeterminate && !invalid && percentageText}

          {helpTextElement}
        </Flex>
      )}
    </div>
  );
}

function useA11y({
  helpText,
  indeterminate,
  value,
  ariaLabel,
}: Pick<ProgressIndicatorProps, 'helpText' | 'indeterminate' | 'value'> & { ariaLabel?: string }) {
  const helpTextId = useId();
  const labelId = useId();
  return {
    helpTextId,
    labelId,
    a11yProps: {
      'aria-describedby': helpText ? helpTextId : undefined,
      'aria-labelledby': ariaLabel ? undefined : labelId,
      'aria-label': ariaLabel,
      'aria-valuenow': indeterminate ? undefined : value,
      role: 'progressbar',
    },
  };
}

function ValueLabel({ align = 'start', value, valueFormat }: ValueLabelProps) {
  return (
    <Text align={align} variant="bodyS">
      {value}
      {valueFormat === 'percentage' && '%'}
    </Text>
  );
}

const barAnimation = keyframes`
  0% {
    left: 0;
  }
  100% {
    left: 50%;
  }
`;

const circleAnimation = keyframes`
  0% {
    transform: rotate(-90deg);
  }
  100% {
    transform: rotate(270deg);
  }
`;
